import {
  computed,
  nextTick,
  onBeforeUnmount,
  onMounted,
  reactive,
  ref,
  watch,
  watchEffect,
} from "vue";
import {
  onBeforeRouteLeave,
  onBeforeRouteUpdate,
  useRoute,
  useRouter,
} from "vue-router";
import { useBreadcrumbStore } from "@/stores/BreadcrumbStore";
import { useAuthStore } from "@/stores/AuthStore";
import apiClient from "@/apiClient";
import { useReferenceStore } from "@/stores/ReferenceStore";
import { usePresenceStore } from "@/stores/PresenceStore";
import {
  deleteResourceDraft,
  getResourceDraft,
  saveResourceDraft,
} from "@/utils/resourceDraftStorage";

// One controller owns both layouts so switching views never duplicates stores or editors.
export function useReferenceController() {
  const route = useRoute();
  const router = useRouter();
  const auth = useAuthStore();
  const breadcrumbStore = useBreadcrumbStore();
  const reference = useReferenceStore();
  const presenceStore = usePresenceStore();
  const history = ref(null);
  const resourceEditorRef = ref(null);
  const cancelModalOpen = ref(false);
  const deleteModalOpen = ref(false);
  const deleteInvoice = ref(false);
  const deletingReference = ref(false);
  const toast = reactive({
    visible: false,
    message: "",
    tone: "success",
  });
  const sectionSaving = reactive({
    overview: false,
    locations: false,
    resources: false,
    description: false,
    notes: false,
  });
  const sectionSnapshots = reactive({
    overview: "",
    locations: "",
    resources: "",
    description: "",
    notes: "",
  });
  const sectionEditing = reactive({
    overview: false,
    locations: false,
    resources: false,
    description: false,
    notes: false,
  });
  const sectionEditButtonClasses =
    "rounded-full border border-sky-200 bg-sky-50 px-4 py-2 text-sm font-semibold text-sky-700 transition hover:border-sky-300 hover:bg-sky-100 dark:border-sky-800 dark:bg-sky-950/40 dark:text-sky-200 dark:hover:border-sky-700 dark:hover:bg-sky-900/60";
  const sectionDoneButtonClasses =
    "rounded-full border border-slate-200 bg-white px-4 py-2 text-sm font-semibold text-slate-600 transition hover:border-slate-300 hover:bg-slate-50 dark:border-slate-700 dark:bg-slate-800 dark:text-slate-200 dark:hover:border-slate-600 dark:hover:bg-slate-700";
  const sectionRefs = ref({});
  const resourceBaseline = ref(null);
  const resourceBaselineKey = ref(null);
  const referenceReady = ref(false);
  const resourceRollbackSnapshot = ref(null);
  const resourceSaveError = ref("");
  const resourceConflict = ref(false);
  const resourceLastSavedAt = ref(null);
  const resourceDraftSavedAt = ref(null);
  const resourceDraftRecovery = ref(null);
  const resourceDraftRestoredAcrossConflict = ref(false);
  const resourceAllocationEditing = ref(false);
  const scheduleHasPendingDateDrafts = ref(false);
  const scheduleEditing = ref(auth.hasAnyRole(["manager", "super"]));
  const canManageSchedule = computed(() =>
    auth.hasAnyRole(["manager", "super"]),
  );
  const isOnline = ref(
    typeof navigator === "undefined" ? true : navigator.onLine,
  );
  let toastTimeout = null;
  let resourceDraftTimeout = null;
  let resourcePresenceInterval = null;
  let resourceSavePromise = null;
  let resourceLeaveApproval = null;

  const canViewPayrollClaims = computed(() =>
    auth.hasAnyRole(["finance-department", "super"]),
  );
  const payrollClaimRows = computed(() => {
    const rows = [];
    (reference.data?.versions ?? []).forEach((version) => {
      (version?.dates ?? []).forEach((date) => {
        (date?.timesheets ?? []).forEach((timesheet) => {
          (timesheet?.claims ?? []).forEach((claim) => {
            rows.push({
              id: claim.id,
              date: date?.date ?? claim.source_date ?? null,
              staffName:
                timesheet?.staff?.contact?.name ??
                timesheet?.claimant_name ??
                claim.source_staff_name ??
                "Staff",
              reference:
                claim.source_reference ??
                timesheet?.shift_reference ??
                reference.data?.txt ??
                "",
              rateCode: claim.rate_code ?? "",
              product: claim.product?.short ?? claim.product?.name ?? "",
              hours: claim.claimed_hours ?? "",
              coWorker:
                claim.co_worker?.contact?.name ?? claim.co_worker_name ?? "",
              callSign: claim.call_sign ?? timesheet?.call_sign ?? "",
              notes: claim.notes ?? "",
              sourceDocument: claim.source_document ?? null,
              source:
                claim.source_sheet && claim.source_row_number
                  ? `${claim.source_sheet} row ${claim.source_row_number}`
                  : "",
            });
          });
        });
      });
    });

    return rows.sort((a, b) =>
      String(a.date ?? "").localeCompare(String(b.date ?? "")),
    );
  });
  const sections = computed(() => [
    { id: "timeline", label: "Timeline" },
    { id: "overview", label: "Overview" },
    { id: "locations", label: "Locations" },
    ...(reference.data?.platform365_journey
      ? [{ id: "platform365-journey", label: "365 Journey" }]
      : []),
    ...(canManageSchedule.value ? [{ id: "schedule", label: "Schedule" }] : []),
    { id: "resources", label: "Resources" },
    ...(canViewPayrollClaims.value
      ? [{ id: "payroll", label: "Payroll" }]
      : []),
    { id: "description", label: "Description" },
    { id: "notes", label: "Notes" },
    { id: "comments", label: "Comments" },
    { id: "history", label: "History" },
  ]);
  const visibleSections = computed(() => sections.value);
  const relatedQuote = computed(() => reference.data?.quote ?? null);
  const canDeleteReference = computed(
    () => auth.hasRole("super") && Boolean(reference.data?.id),
  );
  const linkedInvoice = computed(() => reference.data?.invoice ?? null);
  const hasLinkedInvoice = computed(() => Boolean(linkedInvoice.value?.id));
  const currencyFormatter = new Intl.NumberFormat("en-GB", {
    style: "currency",
    currency: "GBP",
  });
  const formatCurrency = (amount) =>
    currencyFormatter.format((Number(amount) || 0) / 100);
  const linkedInvoiceLabel = computed(() =>
    hasLinkedInvoice.value
      ? `Delete the existing invoice as well (${formatCurrency(linkedInvoice.value?.total ?? 0)})`
      : "",
  );
  const resourceCollaborators = computed(() => {
    const referencePath = `/reference/${route.params.id}`;
    return presenceStore.activeEntries.filter(
      (entry) =>
        Number(entry.id) !== Number(auth.user?.id) &&
        String(entry.current_path ?? "").startsWith(referencePath),
    );
  });

  onMounted(() => {
    reference.configure(route.params.id);
    referenceReady.value = true;
    breadcrumbStore.setValue("");
    window.addEventListener("online", updateOnlineState);
    window.addEventListener("offline", updateOnlineState);
    void refreshResourcePresence();
    startResourcePresencePolling();
  });

  watchEffect(() => {
    if (!reference.isLoading) {
      breadcrumbStore.setValue(reference.name || "");
    }
  });

  const showToast = (message, tone = "success") => {
    toast.message = message;
    toast.tone = tone;
    toast.visible = true;
    if (toastTimeout) {
      clearTimeout(toastTimeout);
    }
    toastTimeout = setTimeout(() => {
      toast.visible = false;
    }, 3000);
  };

  const getAddressText = (value) => {
    if (value === null || value === undefined) {
      return "";
    }

    if (typeof value === "string" || typeof value === "number") {
      return `${value}`;
    }

    if (typeof value !== "object") {
      return "";
    }

    return getAddressText(
      value.data ??
        value.location_address ??
        value.address?.data ??
        value.label ??
        "",
    );
  };

  const normalizeAddressText = (value) =>
    getAddressText(value).trim().replace(/\s+/g, " ");

  const normalizeAddressKey = (value) =>
    normalizeAddressText(value).toLowerCase();

  const normalizeIsoDate = (value) => {
    const stringValue = (value ?? "").toString().trim();
    if (!stringValue) {
      return "";
    }

    const directMatch = stringValue.match(/^(\d{4}-\d{2}-\d{2})(?:$|T)/);
    if (directMatch) {
      return directMatch[1];
    }

    const parsed = new Date(stringValue);

    if (Number.isNaN(parsed.getTime())) {
      return "";
    }

    const year = parsed.getFullYear();
    const month = String(parsed.getMonth() + 1).padStart(2, "0");
    const day = String(parsed.getDate()).padStart(2, "0");
    return `${year}-${month}-${day}`;
  };

  const normalizeTimeValue = (value) => {
    const stringValue = (value ?? "").toString().trim();
    if (!stringValue) {
      return "";
    }

    const parts = stringValue.split(":");
    if (parts.length < 2 || parts.length > 3) {
      return "";
    }

    const [hours, minutes, seconds = "00"] = parts;
    const normalized = [hours, minutes, seconds].map((part) =>
      part.padStart(2, "0"),
    );
    const candidate = normalized.join(":");

    return /^\d{2}:\d{2}:\d{2}$/.test(candidate) ? candidate : "";
  };

  const normalizeResourceDates = () => {
    const activeVersionDates = reference.resources?.dates ?? [];
    activeVersionDates.forEach((entry) => {
      const normalizedDate = normalizeIsoDate(entry?.date);
      if (normalizedDate) {
        entry.date = normalizedDate;
      }

      const normalizedStartTime = normalizeTimeValue(entry?.startTime);
      if (normalizedStartTime) {
        entry.startTime = normalizedStartTime;
      }

      const normalizedFinishTime = normalizeTimeValue(entry?.finishTime);
      if (normalizedFinishTime) {
        entry.finishTime = normalizedFinishTime;
      }
    });
  };

  const hasInvalidResourceDates = () => {
    const activeVersionDates = reference.resources?.dates ?? [];

    return activeVersionDates.some((entry) => !normalizeIsoDate(entry?.date));
  };

  const isAnySectionEditing = computed(
    () =>
      resourceAllocationEditing.value ||
      scheduleEditing.value ||
      isResourceDirty.value ||
      Object.values(sectionEditing).some(Boolean),
  );
  const cloneValue = (value) => JSON.parse(JSON.stringify(value ?? null));

  const setSectionRef = (id, element) => {
    if (!id) {
      return;
    }
    if (element) {
      sectionRefs.value[id] = element;
      return;
    }
    delete sectionRefs.value[id];
  };

  const scrollToSection = async (id) => {
    await nextTick();
    sectionRefs.value[id]?.scrollIntoView({
      behavior: "smooth",
      block: "start",
    });
  };

  const setEditingSection = async (sectionId) => {
    if (sectionEditing.resources && sectionId !== "resources") {
      if (
        isResourceDirty.value &&
        !window.confirm(
          "Leave resource editing? The schedule draft will remain protected on this device.",
        )
      ) {
        return;
      }
      if (isResourceDirty.value) await persistResourceDraft();
      resourceEditorRef.value?.discardPendingDayDateDrafts?.();
      restoreResourceStructure(resourceBaseline.value);
    }
    Object.keys(sectionEditing).forEach((key) => {
      sectionEditing[key] = key === sectionId;
    });
    if (Object.prototype.hasOwnProperty.call(sectionSnapshots, sectionId)) {
      sectionSnapshots[sectionId] =
        sectionId === "resources"
          ? resourceSignature.value
          : getSectionSignature(sectionId);
    }
    if (sectionId === "resources") {
      resourceBaseline.value = cloneValue(getResourceStructurePayload());
      resourceRollbackSnapshot.value = cloneValue(getActiveVersionPayload());
      resourceSaveError.value = "";
      resourceConflict.value = false;
      resourceDraftRestoredAcrossConflict.value = false;
      void loadResourceDraftCandidate();
      void refreshResourcePresence();
      startResourcePresencePolling();
    }
  };

  const stopEditing = async (sectionId) => {
    if (sectionId === "resources") {
      if (
        isResourceDirty.value &&
        !window.confirm("Discard the unsaved schedule changes on this device?")
      ) {
        return false;
      }
      resourceEditorRef.value?.discardPendingDayDateDrafts?.();
      restoreResourceStructure(resourceBaseline.value);
      await clearStoredResourceDraft();
      resourceDraftRecovery.value = null;
      resourceDraftRestoredAcrossConflict.value = false;
    }
    sectionEditing[sectionId] = false;
    return true;
  };

  const getResourceStructurePayload = (version = getActiveVersionPayload()) => {
    return {
      version_id: version?.id ?? null,
      resource_revision: Number(version?.resource_revision ?? 0),
      dates: (version?.dates ?? []).map((entry) => ({
        id: entry?.id ?? entry?.date_id ?? null,
        date_id: entry?.date_id ?? entry?.id ?? null,
        date: normalizeIsoDate(entry?.date) || null,
        startTime: normalizeTimeValue(entry?.startTime) || null,
        finishTime: normalizeTimeValue(entry?.finishTime) || null,
        location_name: entry?.location_name ?? null,
        location_address:
          normalizeAddressText(
            entry?.location_address ?? entry?.address?.data,
          ) || null,
        address_id: entry?.address_id ?? entry?.address?.id ?? null,
        resources: (entry?.resources ?? []).map((resource) => ({
          id: resource?.id ?? null,
          product_id: resource?.product_id ?? resource?.product?.id ?? null,
          quantity: resource?.quantity ?? 1,
        })),
      })),
    };
  };

  const getSectionPayload = (sectionId) => {
    if (sectionId === "overview") {
      return {
        name: reference.data?.name ?? null,
        customer_id:
          reference.data?.customer_id ?? reference.data?.customer?.id ?? null,
      };
    }

    if (sectionId === "locations") {
      return {
        locations: (reference.resources?.locations ?? []).map((entry) => ({
          id: entry?.id ?? null,
          location_address:
            normalizeAddressText(
              entry?.location_address ?? entry?.address?.data,
            ) || null,
          address_id: entry?.address_id ?? entry?.address?.id ?? null,
        })),
      };
    }

    if (sectionId === "resources") {
      return getResourceStructurePayload();
    }

    if (sectionId === "description") {
      return {
        description: reference.data?.description ?? null,
      };
    }

    if (sectionId === "notes") {
      return {
        notes: reference.data?.notes ?? null,
      };
    }

    return null;
  };

  const getActiveVersionPayload = () =>
    reference.data?.versions?.[reference.activeVersion] ??
    reference.resources ??
    null;

  const scheduleSummary = computed(() => {
    const shifts = getActiveVersionPayload()?.dates ?? [];
    const dates = Array.from(
      new Set(
        shifts.map((entry) => normalizeIsoDate(entry?.date)).filter(Boolean),
      ),
    ).sort();
    const locations = new Set(
      shifts
        .map((entry) =>
          normalizeAddressText(entry?.location_address ?? entry?.address?.data),
        )
        .filter(Boolean),
    );
    const formatDate = (value) =>
      value
        ? new Date(`${value}T12:00:00`).toLocaleDateString("en-GB", {
            day: "numeric",
            month: "short",
            year: "numeric",
          })
        : "Not scheduled";

    return {
      dayCount: dates.length,
      shiftCount: shifts.length,
      locationCount: locations.size,
      dateRange:
        dates.length > 1
          ? `${formatDate(dates[0])} – ${formatDate(dates.at(-1))}`
          : formatDate(dates[0]),
    };
  });

  const getSectionRequestPayload = (sectionId) => {
    const activeVersion = getActiveVersionPayload();

    if (sectionId === "locations" && activeVersion?.id) {
      return reference.buildManagePayload({
        versions: [
          {
            id: activeVersion.id,
            dates: (activeVersion?.dates ?? []).map((entry) => ({
              id: entry?.id ?? entry?.date_id ?? null,
              date_id: entry?.date_id ?? entry?.id ?? null,
              date: entry?.date ?? null,
              startTime: entry?.startTime ?? null,
              finishTime: entry?.finishTime ?? null,
              location_name: entry?.location_name ?? null,
              location_address:
                normalizeAddressText(
                  entry?.location_address ?? entry?.address?.data,
                ) || null,
              address_id: entry?.address_id ?? entry?.address?.id ?? null,
              latitude: entry?.address?.latitude ?? null,
              longitude: entry?.address?.longitude ?? null,
            })),
            locations: (activeVersion?.locations ?? []).map((entry) => ({
              id: entry?.id ?? null,
              address_id: entry?.address_id ?? entry?.address?.id ?? null,
              location_address:
                normalizeAddressText(
                  entry?.location_address ?? entry?.address?.data,
                ) || null,
              latitude: entry?.address?.latitude ?? null,
              longitude: entry?.address?.longitude ?? null,
            })),
          },
        ],
        includeResources: false,
        includeTimesheets: false,
      });
    }

    if (sectionId === "resources" && activeVersion?.id) {
      return reference.buildManagePayload({
        versions: [activeVersion],
        includeTimesheets: false,
        includePersonnelAssignments: false,
      });
    }

    if (
      sectionId === "overview" ||
      sectionId === "description" ||
      sectionId === "notes"
    ) {
      return reference.buildManagePayload({
        versions: activeVersion?.id ? [activeVersion] : undefined,
        includeResources: false,
        includeTimesheets: false,
      });
    }

    return reference.buildManagePayload();
  };

  const getSectionSignature = (sectionId) =>
    JSON.stringify(getSectionPayload(sectionId) ?? null);

  const resourceDraftKey = computed(() => {
    const versionId = getActiveVersionPayload()?.id;
    return reference.data?.id && versionId
      ? `reference:${reference.data.id}:version:${versionId}`
      : null;
  });
  // Revisions are server metadata, not user edits.
  const getResourceSignature = (structure = getResourceStructurePayload()) => {
    const compare = (left, right) =>
      JSON.stringify(left).localeCompare(JSON.stringify(right));
    return JSON.stringify({
      version_id: structure.version_id,
      dates: structure.dates
        .map((entry) => ({
          ...entry,
          date: normalizeIsoDate(entry.date) || null,
          startTime: normalizeTimeValue(entry.startTime) || null,
          finishTime: normalizeTimeValue(entry.finishTime) || null,
          resources: [...entry.resources].sort(compare),
        }))
        .sort(compare),
    });
  };
  const resourceSignature = computed(() => getResourceSignature());
  const isResourceDirty = computed(
    () =>
      scheduleHasPendingDateDrafts.value ||
      (Boolean(sectionSnapshots.resources) &&
        resourceSignature.value !== sectionSnapshots.resources),
  );

  const openScheduleManager = () => {
    if (!canManageSchedule.value) return;
    if (!isResourceDirty.value) {
      resourceBaseline.value = cloneValue(getResourceStructurePayload());
      resourceRollbackSnapshot.value = cloneValue(getActiveVersionPayload());
      sectionSnapshots.resources = resourceSignature.value;
    }
    scheduleEditing.value = true;
  };

  const closeScheduleManager = async () => {
    if (!isResourceDirty.value) {
      resourceEditorRef.value?.discardPendingDayDateDrafts?.();
      scheduleEditing.value = false;
      return;
    }
    const closed = await stopEditing("resources");
    if (closed) scheduleEditing.value = false;
  };
  const resourceChangedDayCount = computed(() => {
    const baselineDates = resourceBaseline.value?.dates ?? [];
    const currentDates = getResourceStructurePayload().dates ?? [];
    const baselineById = new Map(
      baselineDates.map((entry, index) => [
        String(entry.id ?? entry.date_id ?? `draft-${index}`),
        JSON.stringify(entry),
      ]),
    );
    let changed = 0;
    currentDates.forEach((entry, index) => {
      const key = String(entry.id ?? entry.date_id ?? `draft-${index}`);
      if (baselineById.get(key) !== JSON.stringify(entry)) changed += 1;
      baselineById.delete(key);
    });
    return changed + baselineById.size;
  });
  const resourceSaveStatus = computed(() => {
    if (!isOnline.value) return "Offline — draft protected on this device";
    if (sectionSaving.resources) return "Saving schedule…";
    if (resourceSaveError.value) return resourceSaveError.value;
    if (resourceDraftRestoredAcrossConflict.value) {
      return "Reviewing a draft based on an older schedule — saving requires confirmation";
    }
    if (isResourceDirty.value) {
      const count = resourceChangedDayCount.value;
      return `${count || 1} day${count === 1 ? "" : "s"} with unsaved schedule changes`;
    }
    if (resourceLastSavedAt.value) {
      return `Schedule saved at ${resourceLastSavedAt.value.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}`;
    }
    return "Schedule is up to date";
  });
  const resourceSaveStatusTone = computed(() => {
    if (
      !isOnline.value ||
      resourceSaveError.value ||
      resourceDraftRestoredAcrossConflict.value
    )
      return "error";
    if (sectionSaving.resources) return "saving";
    if (isResourceDirty.value) return "dirty";
    return "saved";
  });

  const updateOnlineState = () => {
    isOnline.value = navigator.onLine;
  };

  const restoreResourceStructure = (structure) => {
    const activeVersion = getActiveVersionPayload();
    if (!activeVersion || !structure?.dates) return;

    const currentDates = activeVersion.dates ?? [];
    const currentDatesById = new Map(
      currentDates
        .filter((entry) => entry?.id ?? entry?.date_id)
        .map((entry) => [Number(entry.id ?? entry.date_id), entry]),
    );
    const rollbackDatesById = new Map(
      (resourceRollbackSnapshot.value?.dates ?? [])
        .filter((entry) => entry?.id ?? entry?.date_id)
        .map((entry) => [Number(entry.id ?? entry.date_id), entry]),
    );
    const rollbackResourcesById = new Map(
      (resourceRollbackSnapshot.value?.dates ?? [])
        .flatMap((entry) => entry?.resources ?? [])
        .filter((resource) => resource?.id)
        .map((resource) => [Number(resource.id), resource]),
    );

    activeVersion.dates = structure.dates.map((draftDate) => {
      const dateId = Number(draftDate.id ?? draftDate.date_id);
      const currentDate = currentDatesById.get(dateId);
      const rollbackDate = rollbackDatesById.get(dateId);
      const currentResourcesById = new Map(
        (currentDate?.resources ?? [])
          .filter((resource) => resource?.id)
          .map((resource) => [Number(resource.id), resource]),
      );
      return {
        ...(rollbackDate ?? {}),
        ...(currentDate ?? {}),
        ...cloneValue(draftDate),
        resources: (draftDate.resources ?? []).map((draftResource) => ({
          ...(rollbackResourcesById.get(Number(draftResource.id)) ?? {}),
          ...(currentResourcesById.get(Number(draftResource.id)) ?? {}),
          ...cloneValue(draftResource),
        })),
      };
    });
  };

  const loadResourceDraftCandidate = async () => {
    const draft = await getResourceDraft(resourceDraftKey.value);
    const hasPendingDayDates =
      Object.keys(draft?.structure?.pending_day_dates ?? {}).length > 0;
    if (
      !draft?.structure ||
      (getResourceSignature(draft.structure) === sectionSnapshots.resources &&
        !hasPendingDayDates)
    )
      return;
    resourceDraftRecovery.value = {
      ...draft,
      revisionMismatch:
        Number(draft.base_revision ?? 0) !==
        Number(getActiveVersionPayload()?.resource_revision ?? 0),
    };
  };

  watch(
    [resourceDraftKey, () => reference.isLoading, referenceReady],
    async ([draftKey, loading]) => {
      if (
        !referenceReady.value ||
        !draftKey ||
        loading ||
        String(reference.data?.id) !== String(route.params.id)
      )
        return;
      if (resourceBaselineKey.value === draftKey && isResourceDirty.value)
        return;
      resourceBaselineKey.value = draftKey;
      resourceBaseline.value = cloneValue(getResourceStructurePayload());
      resourceRollbackSnapshot.value = cloneValue(getActiveVersionPayload());
      sectionSnapshots.resources = resourceSignature.value;
      await loadResourceDraftCandidate();
    },
    { immediate: true },
  );

  const restoreResourceDraft = () => {
    if (!resourceDraftRecovery.value) return;
    const revisionMismatch = resourceDraftRecovery.value.revisionMismatch;
    const pendingDayDates =
      resourceDraftRecovery.value.structure?.pending_day_dates ?? {};
    restoreResourceStructure(resourceDraftRecovery.value.structure);
    void nextTick(() => {
      resourceEditorRef.value?.restorePendingDayDateDrafts?.(pendingDayDates);
    });
    resourceDraftRecovery.value = null;
    resourceDraftRestoredAcrossConflict.value = revisionMismatch;
    showToast(
      revisionMismatch
        ? "Recovered draft loaded for review. Saving it will require confirmation."
        : "Recovered the protected schedule draft.",
      revisionMismatch ? "error" : "success",
    );
  };

  const clearStoredResourceDraft = async () => {
    if (resourceDraftTimeout) {
      clearTimeout(resourceDraftTimeout);
      resourceDraftTimeout = null;
    }
    await deleteResourceDraft(resourceDraftKey.value);
    resourceDraftSavedAt.value = null;
  };

  const discardRecoveredResourceDraft = async () => {
    await clearStoredResourceDraft();
    resourceDraftRecovery.value = null;
  };

  const reloadResourceAfterConflict = async () => {
    await persistResourceDraft();
    await reference.fetch();
    resourceBaseline.value = cloneValue(getResourceStructurePayload());
    resourceRollbackSnapshot.value = cloneValue(getActiveVersionPayload());
    sectionSnapshots.resources = resourceSignature.value;
    resourceSaveError.value = "";
    resourceConflict.value = false;
    resourceDraftRestoredAcrossConflict.value = false;
    await loadResourceDraftCandidate();
  };

  const persistResourceDraft = async () => {
    if (!isResourceDirty.value || !resourceDraftKey.value) return;
    const savedAt = new Date();
    const structure = getResourceStructurePayload();
    structure.pending_day_dates =
      resourceEditorRef.value?.getPendingDayDateDrafts?.() ?? {};
    await saveResourceDraft(resourceDraftKey.value, {
      base_revision: Number(resourceBaseline.value?.resource_revision ?? 0),
      saved_at: savedAt.toISOString(),
      signature: resourceSignature.value,
      structure,
    });
    resourceDraftSavedAt.value = savedAt;
  };

  const queueResourceSave = () => {
    resourceLeaveApproval = null;
    if (!resourceConflict.value) resourceSaveError.value = "";
    if (resourceDraftTimeout) clearTimeout(resourceDraftTimeout);
    resourceDraftTimeout = null;
    if (!isResourceDirty.value || !canManageSchedule.value) return;
    resourceDraftTimeout = setTimeout(async () => {
      resourceDraftTimeout = null;
      await persistResourceDraft();
      await saveResourceSchedule({ automatic: true });
    }, 750);
  };
  watch(resourceSignature, queueResourceSave);

  const setScheduleDateDraftsDirty = (dirty) => {
    scheduleHasPendingDateDrafts.value = dirty;
    queueResourceSave();
  };
  watch(isOnline, (online) => {
    if (online) queueResourceSave();
  });

  const refreshResourcePresence = async () => {
    await presenceStore.load();
  };

  const stopResourcePresencePolling = () => {
    if (resourcePresenceInterval)
      window.clearInterval(resourcePresenceInterval);
    resourcePresenceInterval = null;
  };

  const startResourcePresencePolling = () => {
    stopResourcePresencePolling();
    resourcePresenceInterval = window.setInterval(() => {
      void refreshResourcePresence();
    }, 30000);
  };

  const resourceLeaveMessage =
    "This resource schedule has unsaved changes. They are protected on this device, but have not been saved to the job. Leave anyway?";
  const handleResourceBeforeUnload = (event) => {
    if (!isResourceDirty.value) return undefined;
    event.preventDefault();
    event.returnValue = resourceLeaveMessage;
    return resourceLeaveMessage;
  };

  watch(isResourceDirty, (dirty) => {
    if (dirty) {
      window.addEventListener("beforeunload", handleResourceBeforeUnload);
    } else {
      window.removeEventListener("beforeunload", handleResourceBeforeUnload);
    }
  });

  const confirmReferenceLeave = async () => {
    if (!isResourceDirty.value) return true;
    const leaveSignature = () =>
      JSON.stringify([
        resourceSignature.value,
        resourceEditorRef.value?.getPendingDayDateDrafts?.() ?? {},
      ]);
    if (resourceLeaveApproval === leaveSignature()) return true;
    if (resourceDraftTimeout) clearTimeout(resourceDraftTimeout);
    resourceDraftTimeout = null;
    await persistResourceDraft();
    while (
      isResourceDirty.value &&
      (await saveResourceSchedule({ automatic: true }))
    ) {
      // Flush edits made while the previous request was in flight, too.
    }
    if (!isResourceDirty.value) return true;
    const shouldLeave = window.confirm(resourceLeaveMessage);
    if (shouldLeave) {
      await persistResourceDraft();
      resourceLeaveApproval = leaveSignature();
    }
    return shouldLeave;
  };
  // A redirect is part of the same navigation. Clear the approval only when
  // navigation settles, so failed/offline saves warn once across redirects.
  const removeResourceAfterEach = router.afterEach?.(() => {
    resourceLeaveApproval = null;
  });
  onBeforeRouteLeave(confirmReferenceLeave);
  onBeforeRouteUpdate((to, from) => {
    // Query-only tab navigation stays within this editor; a different job does not.
    if (String(to.params.id) !== String(from.params.id))
      return confirmReferenceLeave();
    return true;
  });

  const saveResourceSchedule = async ({ automatic = false } = {}) => {
    if (resourceSavePromise) {
      const saved = await resourceSavePromise;
      if (!saved || !isResourceDirty.value) return saved;
      return saveResourceSchedule({ automatic });
    }
    if (!canManageSchedule.value || !isOnline.value || resourceConflict.value)
      return false;
    if (resourceDraftRestoredAcrossConflict.value) {
      if (
        automatic ||
        !window.confirm(
          "This recovered draft was based on an older schedule. Saving will replace newer structural changes. Have you reviewed the affected days and want to continue?",
        )
      )
        return false;
    }
    const draftResult = resourceEditorRef.value?.applyPendingDayDateDrafts?.();
    if (draftResult?.invalid || hasInvalidResourceDates()) {
      resourceSaveError.value =
        "One or more resource dates are invalid. Fix them before saving.";
      if (!automatic) showToast(resourceSaveError.value, "error");
      await persistResourceDraft();
      return false;
    }
    normalizeResourceDates();
    if (!isResourceDirty.value) {
      await clearStoredResourceDraft();
      return true;
    }

    const version = getActiveVersionPayload();
    const submittedVersion = cloneValue(version);
    const submittedRows = (version.dates ?? []).map((entry) => ({
      entry,
      resources: [...(entry.resources ?? [])],
    }));
    const payload = cloneValue(getSectionRequestPayload("resources"));
    // The API preserves other sections, including edits saved by another user.
    payload.schedule_only = true;
    delete payload.versions[0].locations;
    sectionSaving.resources = true;
    resourceSaveError.value = "";
    let written = false;
    resourceSavePromise = (async () => {
      try {
        await reference.update(payload);
        written = true;
        const savedVersion = await reference.refreshResourceSchedule(
          version.id,
          submittedVersion,
          submittedRows,
        );
        resourceBaseline.value = cloneValue(
          getResourceStructurePayload(savedVersion),
        );
        resourceRollbackSnapshot.value = cloneValue(savedVersion);
        sectionSnapshots.resources = getResourceSignature(
          resourceBaseline.value,
        );
        resourceLastSavedAt.value = new Date();
        resourceDraftRestoredAcrossConflict.value = false;
        sectionEditing.resources = false;
        if (isResourceDirty.value) {
          await persistResourceDraft();
          queueResourceSave();
        } else {
          await clearStoredResourceDraft();
        }
        if (!automatic) showToast("Reference saved.");
        history.value?.refresh?.();
        return true;
      } catch (error) {
        if (resourceDraftTimeout) clearTimeout(resourceDraftTimeout);
        resourceDraftTimeout = null;
        resourceSaveError.value =
          error?.response?.status === 409
            ? "Another user changed this schedule. Your draft is safe on this device; reload before applying it."
            : (error?.response?.data?.message ??
              error?.message ??
              "Unable to save reference.");
        // If the write succeeded but its IDs could not be loaded, require a
        // reload rather than resubmitting new rows without their saved IDs.
        resourceConflict.value = written || error?.response?.status === 409;
        await persistResourceDraft();
        if (!automatic) showToast(resourceSaveError.value, "error");
        console.error(error);
        return false;
      } finally {
        sectionSaving.resources = false;
        resourceSavePromise = null;
      }
    })();
    return resourceSavePromise;
  };

  const saveSection = async (sectionId) => {
    if (sectionId === "resources") {
      const saved = await saveResourceSchedule();
      if (saved && !isResourceDirty.value) scheduleEditing.value = false;
      return;
    }
    if (sectionSaving[sectionId]) return;
    if (getSectionSignature(sectionId) === sectionSnapshots[sectionId]) {
      sectionEditing[sectionId] = false;
      showToast("No changes to save.");
      return;
    }
    sectionSaving[sectionId] = true;
    try {
      await reference.update(getSectionRequestPayload(sectionId));
      sectionEditing[sectionId] = false;
      sectionSnapshots[sectionId] = getSectionSignature(sectionId);
      showToast("Reference saved.");
      history.value?.refresh?.();
    } catch (error) {
      showToast(
        error?.response?.data?.message ?? "Unable to save reference.",
        "error",
      );
      console.error(error);
    } finally {
      sectionSaving[sectionId] = false;
    }
  };

  const setVersionAccepted = async () => {
    if (!confirm("Are you sure you want to accept this version?")) {
      return;
    }

    await reference.acceptVersion();
    history.value?.refresh?.();
    window.triggerConfetti();
  };

  const setVersionCancelled = async () => {
    cancelModalOpen.value = false;
    await reference.cancelVersion();
    history.value?.refresh?.();
    window.triggerBadNews();
  };

  const openCancelModal = () => {
    cancelModalOpen.value = true;
  };

  const closeCancelModal = () => {
    cancelModalOpen.value = false;
  };

  const openDeleteModal = () => {
    deleteInvoice.value = false;
    deleteModalOpen.value = true;
  };

  const closeDeleteModal = () => {
    if (deletingReference.value) {
      return;
    }

    deleteModalOpen.value = false;
    deleteInvoice.value = false;
  };

  const destroyReference = async () => {
    if (deletingReference.value || !reference.data?.id) {
      return;
    }

    deletingReference.value = true;

    try {
      await reference.destroy({
        delete_invoice: deleteInvoice.value,
      });
      deleteModalOpen.value = false;
      deleteInvoice.value = false;
      await router.push("/job");
    } catch (error) {
      showToast(
        error?.response?.data?.message ?? "Unable to delete reference.",
        "error",
      );
      console.error(error);
    } finally {
      deletingReference.value = false;
    }
  };

  const referenceLocations = computed(() => {
    const versions = [];
    if (
      reference.data?.accepted_version?.dates?.length ||
      reference.data?.accepted_version?.locations?.length
    ) {
      versions.push(reference.data.accepted_version);
    }
    if (
      reference.data?.acceptedVersion?.dates?.length ||
      reference.data?.acceptedVersion?.locations?.length
    ) {
      versions.push(reference.data.acceptedVersion);
    }
    if (Array.isArray(reference.data?.versions)) {
      versions.push(...reference.data.versions);
    }
    if (
      reference.resources?.dates?.length ||
      reference.resources?.locations?.length
    ) {
      versions.push(reference.resources);
    }

    const seen = new Set();
    const locations = [];

    versions.forEach((version) => {
      (version?.locations ?? []).forEach((location, index) => {
        const addressRecord = location?.address ?? null;
        const address = normalizeAddressText(
          addressRecord?.data ?? location?.location_address,
        );
        if (!address) return;

        const key = normalizeAddressKey(address);
        if (!key || seen.has(key)) return;

        seen.add(key);
        locations.push({
          id:
            location?.id ??
            `reference-version-location-${index}-${locations.length}`,
          data: address,
          label: address,
          address_id: addressRecord?.id ?? location?.address_id ?? null,
          latitude: addressRecord?.latitude ?? null,
          longitude: addressRecord?.longitude ?? null,
          source: "version",
        });
      });

      (version?.dates ?? []).forEach((date, index) => {
        const addressRecord = date?.address ?? null;
        const address = normalizeAddressText(
          addressRecord?.data ?? date?.location_address,
        );
        const name = (date?.location_name ?? "").toString().trim();
        const displayValue = address || name;
        if (!displayValue) return;

        const key = normalizeAddressKey(displayValue);
        if (!key || seen.has(key)) return;

        seen.add(key);
        locations.push({
          id: `reference-location-${index}-${locations.length}`,
          data: address || name,
          label: displayValue,
          address_id: addressRecord?.id ?? date?.address_id ?? null,
          latitude: addressRecord?.latitude ?? null,
          longitude: addressRecord?.longitude ?? null,
          base_distance_miles: date?.base_distance_miles ?? null,
          base_duration_minutes: date?.base_duration_minutes ?? null,
          source: "date",
        });
      });
    });

    return locations;
  });

  const referenceLocationAddresses = computed(() =>
    referenceLocations.value.filter((location) => location.data),
  );
  const getActiveVersionLocations = () => {
    const activeVersion = getActiveVersionPayload();
    if (!activeVersion) {
      return null;
    }

    if (!Array.isArray(activeVersion.locations)) {
      activeVersion.locations = [];
    }

    return activeVersion.locations;
  };

  const normalizeLocationPayload = (location) => {
    if (location && typeof location === "object") {
      return {
        id: location.id ?? location.address_id ?? null,
        data: normalizeAddressText(
          location.data ?? location.location_address ?? location.address?.data,
        ),
        latitude: location.latitude ?? null,
        longitude: location.longitude ?? null,
      };
    }

    return {
      id: null,
      data: (location ?? "").toString().trim(),
      latitude: null,
      longitude: null,
    };
  };

  const findExistingReferenceAddress = (addressText) => {
    const targetKey = normalizeAddressKey(addressText);
    if (!targetKey) {
      return null;
    }

    return (
      referenceLocationAddresses.value.find(
        (location) =>
          normalizeAddressKey(location.data) === targetKey &&
          location.address_id,
      ) ?? null
    );
  };

  const ensureReferenceAddress = async (locationPayload) => {
    const existing = locationPayload.id
      ? locationPayload
      : findExistingReferenceAddress(locationPayload.data);

    if (existing?.id || existing?.address_id) {
      return {
        id: existing.id ?? existing.address_id,
        data: existing.data,
        latitude: existing.latitude ?? null,
        longitude: existing.longitude ?? null,
      };
    }

    const response = await apiClient.post("/api/address", {
      data: locationPayload.data,
      latitude: locationPayload.latitude,
      longitude: locationPayload.longitude,
    });

    return response.data;
  };

  const addLocationToReferenceLocations = async (location) => {
    const locationPayload = normalizeLocationPayload(location);
    const trimmedAddress = locationPayload.data;
    if (!trimmedAddress) {
      return;
    }

    const activeLocations = getActiveVersionLocations();
    if (!activeLocations) {
      showToast(
        "No active reference version is available for locations.",
        "error",
      );
      return;
    }

    try {
      const address = await ensureReferenceAddress(locationPayload);
      const targetKey = normalizeAddressKey(address?.data ?? trimmedAddress);
      const exists = activeLocations.some(
        (entry) =>
          normalizeAddressKey(
            entry?.location_address ?? entry?.address?.data ?? "",
          ) === targetKey,
      );

      if (exists) {
        showToast(
          "That location is already available on this reference.",
          "error",
        );
        return;
      }

      activeLocations.push({
        id: null,
        address_id: address?.id ?? null,
        location_address: address?.data ?? trimmedAddress,
        address: address ?? null,
      });

      showToast(
        "Location added. Save the section to keep it on this reference.",
      );
    } catch (error) {
      showToast(
        error?.response?.data?.message ?? "Unable to save location address.",
        "error",
      );
      console.error(error);
    }
  };

  onBeforeUnmount(() => {
    removeResourceAfterEach?.();
    if (toastTimeout) {
      clearTimeout(toastTimeout);
    }
    if (resourceDraftTimeout) {
      clearTimeout(resourceDraftTimeout);
    }
    stopResourcePresencePolling();
    window.removeEventListener("beforeunload", handleResourceBeforeUnload);
    window.removeEventListener("online", updateOnlineState);
    window.removeEventListener("offline", updateOnlineState);
  });

  return {
    router,
    auth,
    reference,
    history,
    resourceEditorRef,
    cancelModalOpen,
    deleteModalOpen,
    deleteInvoice,
    deletingReference,
    toast,
    showToast,
    sectionSaving,
    sectionEditing,
    sectionEditButtonClasses,
    sectionDoneButtonClasses,
    resourceConflict,
    resourceDraftSavedAt,
    resourceDraftRecovery,
    resourceAllocationEditing,
    scheduleHasPendingDateDrafts,
    setScheduleDateDraftsDirty,
    scheduleEditing,
    canManageSchedule,
    isOnline,
    canViewPayrollClaims,
    payrollClaimRows,
    visibleSections,
    relatedQuote,
    canDeleteReference,
    hasLinkedInvoice,
    linkedInvoiceLabel,
    resourceCollaborators,
    isAnySectionEditing,
    setSectionRef,
    scrollToSection,
    setEditingSection,
    stopEditing,
    scheduleSummary,
    isResourceDirty,
    openScheduleManager,
    closeScheduleManager,
    resourceSaveStatus,
    resourceSaveStatusTone,
    restoreResourceDraft,
    discardRecoveredResourceDraft,
    reloadResourceAfterConflict,
    saveSection,
    setVersionAccepted,
    setVersionCancelled,
    openCancelModal,
    closeCancelModal,
    openDeleteModal,
    closeDeleteModal,
    destroyReference,
    referenceLocations,
    referenceLocationAddresses,
    addLocationToReferenceLocations,
    resourceSaveError,
  };
}
